import { Fragment } from "react";
import { Platform } from "../..";
import {
    FilterMetadata,
    FilterOperators,
    SortDirections,
} from "~/modules/filters/types";
import { VisualizationGrid } from "~/modules/visualization/components/VisualizationGrid";
import { PlatformHeader } from "./PlatformHeader";
import { PlatformIndividualAggregates } from "./PlatformIndividualAggregates";
import { Props as AggregateDisplayProps } from "~/components/AggregateDisplay";
import * as Analytics from "../../analytics";
import { DEFAULT_ASSET_LIST_QUERY } from "~/modules/assets/consts";
import { PlatformAssets } from "./PlatformAssets";
import { AssetColumns } from "~/modules/assets/types";
import { faInfoCircle } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    TokenTimeseriesColumns,
    TokenTimeseriesGroupBy,
} from "~/modules/token_timeseries/types";
import { MetricTimeseriesChart } from "~/modules/visualization/components/MetricTimeseriesChart";
import { MetricTypes } from "~/modules/visualization/components/MetricTimeseriesChart/types";
import { ASSET_CLASS_IDS, PROTOCOL_IDS, TOKENIZATION_TYPE_IDS } from "~/consts";
import {
    LeagueTableGroupTypes,
    LeagueTableTabsByDistributionType,
} from "~/modules/league_tables/types";
import { HomePageLeagueTable } from "~/modules/main/pages/home/HomePageLeagueTable";
import { LeagueTablePieChart } from "~/modules/league_tables/components/LeagueTablePieChart";
import { DashboardUnlockBoundary } from "~/modules/dashboard_page_unlocks/components/DashboardUnlockBoundary";
import { DashboardPageUnlockGate } from "~/modules/dashboard_page_unlocks/components/DashboardPageUnlockGate";
import { getPlatformURL } from "~/routes";

// // // //

function ComingSoonCard() {
    return (
        <div className="rounded-md bg-blue-50 p-4 mt-6 border border-blue-200">
            <div className="flex">
                <div className="shrink-0">
                    <FontAwesomeIcon
                        icon={faInfoCircle}
                        className="size-5 text-blue-400"
                    />
                </div>
                <div className="ml-3 flex-1">
                    <p className="text-sm text-blue-700">
                        Market data and analytics coming soon
                    </p>
                </div>
            </div>
        </div>
    );
}

// // // //

interface Props {
    platform: Platform;
    assetFilterMetadata: FilterMetadata[];
    leagueTableTabs: LeagueTableTabsByDistributionType;
    aggregates: Array<AggregateDisplayProps>;
}

export function PlatformIndividualPage(props: Props) {
    const { platform, assetFilterMetadata, leagueTableTabs, aggregates } =
        props;

    const hasData =
        platform.circulating_asset_value_dollar !== null ||
        platform.bridged_token_market_cap_dollar !== null;

    const hasDistributedData = platform.tokenization_type_ids.includes(
        TOKENIZATION_TYPE_IDS.OnChainDistributed,
    );

    const hasRepresentedData = platform.tokenization_type_ids.includes(
        TOKENIZATION_TYPE_IDS.OnChainRepresented,
    );

    const hasStablecoinsData =
        platform.asset_class_ids?.includes(ASSET_CLASS_IDS.Stablecoin) ?? false;

    const hasOnlyStablecoinsData =
        hasStablecoinsData && platform.asset_class_ids?.length === 1;

    // Define flag to check if the platform is Securitize
    const IS_SECURITIZE = platform.id === PROTOCOL_IDS.Securitize;

    // Default to asset class for Securitize, network for other platforms
    const defaultPiechartGroupBy = IS_SECURITIZE
        ? LeagueTableGroupTypes.assetClasses
        : LeagueTableGroupTypes.networks;

    return (
        <Fragment>
            <PlatformHeader platform={platform} />

            {!hasData && (
                <div className="h-[calc(100vh-22rem)]">
                    <ComingSoonCard />
                </div>
            )}

            {hasData && (
                <Fragment>
                    <PlatformIndividualAggregates aggregates={aggregates} />

                    <VisualizationGrid className="my-6">
                        <MetricTimeseriesChart
                            title={`${platform.name} Metrics`}
                            analyticsChartName="platform-individual-metrics"
                            filters={[
                                {
                                    field: TokenTimeseriesColumns.protocolID,
                                    operator: FilterOperators.Equals,
                                    value: platform.id,
                                },
                            ]}
                            columnSpanConfig={{
                                sm: 12,
                                md: 12,
                                lg: 12,
                                xl: 12,
                                xxl: 12,
                            }}
                            controls={{
                                metrics: {
                                    options: [
                                        MetricTypes.TotalValue,
                                        MetricTypes.AssetHolders,
                                        MetricTypes.ActiveAddresses,
                                        MetricTypes.TransferVolume,
                                        MetricTypes.MintBurnVolume,
                                    ],
                                },
                                groupBy: {
                                    enabled: false,
                                    value: TokenTimeseriesGroupBy.asset,
                                },
                                distributionSelect: {
                                    distributed: {
                                        enabled: hasDistributedData,
                                    },
                                    represented: {
                                        enabled: hasRepresentedData,
                                    },
                                    stablecoins: {
                                        enabled:
                                            hasStablecoinsData &&
                                            !hasOnlyStablecoinsData,
                                        active: hasOnlyStablecoinsData,
                                    },
                                },
                            }}
                        />

                        <HomePageLeagueTable
                            title={`${platform.name} RWA League Table`}
                            analyticsTableName="platform-league-table"
                            leagueTableTabs={leagueTableTabs}
                            filterEmptyTabs={false}
                            colSpans={{
                                sm: 12,
                                md: 12,
                                lg: 12,
                                xl: 7,
                                xxl: 6,
                            }}
                        />

                        <LeagueTablePieChart
                            title="Total RWA Value"
                            colSpans={{
                                sm: 12,
                                md: 12,
                                lg: 12,
                                xl: 5,
                                xxl: 6,
                            }}
                            leagueTableTabs={leagueTableTabs}
                            defaultGroupBy={defaultPiechartGroupBy}
                            allowedGroupByValues={[
                                LeagueTableGroupTypes.assetClasses,
                                LeagueTableGroupTypes.networks,
                            ]}
                            onChangeGroupBy={(obj) => {
                                Analytics.trackChangePieChartGroupBy({
                                    platformSlug: platform.slug,
                                    prevValue: obj.prevValue,
                                    nextValue: obj.nextValue,
                                });
                            }}
                        />
                    </VisualizationGrid>

                    <PlatformAssets
                        platform={platform}
                        filterMetadata={assetFilterMetadata}
                        assetsQuery={{
                            ...DEFAULT_ASSET_LIST_QUERY,
                            pagination: {
                                page: 1,
                                perPage: 25,
                            },
                            sort: {
                                field: AssetColumns.circulatingAssetValueDollar,
                                direction: SortDirections.desc,
                            },
                        }}
                    />
                </Fragment>
            )}

            {/* TODO - remove DashboardPageUnlockGate when we're ready to set this live for everyone */}
            <DashboardPageUnlockGate>
                <DashboardUnlockBoundary
                    pathname={getPlatformURL({
                        platformSlug: platform.slug,
                    })}
                />
            </DashboardPageUnlockGate>
        </Fragment>
    );
}
